import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';
import {reactPlugin} from '@datadog/browser-rum-react';
import {FETCH_FAILURE_MESSAGE_RE} from 'utils/consts';
import {shouldSkipDatadog} from 'utils/environment';

const hasUsableDatadogId = (user) => Boolean(user?.id);

export const buildDatadogUser = (payload = {}) => {
  if (!payload || typeof payload !== 'object') return null;

  const userInfo = payload.user_info || null;
  const id = userInfo?.sub || payload.employee_id || null;
  const email = userInfo?.email || null;
  const name = userInfo?.name || null;
  const employeeId = payload.employee_id || null;
  const uncommonRole = userInfo?.uncommonrole || null;
  const locationWid = userInfo?.location_wid || null;
  const permissions = payload.permissions || null;
  const psSiteCodes = payload.ps_site_codes?.length
    ? payload.ps_site_codes
    : null;
  const permissionsSource = payload.permissions_source || null;

  const datadogUser = {
    ...(id ? {id} : {}),
    ...(email ? {email} : {}),
    ...(name ? {name} : {}),
    ...(employeeId ? {employee_id: employeeId} : {}),
    ...(uncommonRole ? {uncommonrole: uncommonRole} : {}),
    ...(locationWid ? {location_wid: locationWid} : {}),
    ...(permissions ? {permissions} : {}),
    ...(psSiteCodes ? {ps_site_codes: psSiteCodes} : {}),
    ...(permissionsSource ? {permissions_source: permissionsSource} : {}),
  };

  return Object.keys(datadogUser).length ? datadogUser : null;
};

/**
 * Call once during app init (e.g., in index.js)
 */
export const initializeDatadog = () => {
  // Skip initialization in development/synthetic environments
  if (shouldSkipDatadog()) {
    console.debug(
      'Datadog skipped: Development/synthetic environment detected'
    );
    return;
  }

  // Skip if already initialized
  if (window.DD_RUM?.getInitConfiguration()) {
    console.debug('Datadog already initialized');
    return;
  }

  const appId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;

  if (!appId || !clientToken) {
    console.error(
      'Datadog initialization skipped: Missing application ID or client token'
    );
    return;
  }

  try {
    datadogRum.init({
      // Datadog headers only: track-be's CORS allow-list doesn't include the
      // `tracestate`/`baggage` headers that the default propagators add.
      allowedTracingUrls: process.env.REACT_APP_TRACK_API_BASE_URL
        ? [
            {
              match: process.env.REACT_APP_TRACK_API_BASE_URL,
              propagatorTypes: ['datadog'],
            },
          ]
        : [],
      applicationId: appId,
      // Transport-level fetch failures are already recorded as failed
      // resources (trackResources); as errors they're mostly users going
      // offline or a redirect aborting in-flight requests.
      beforeSend: (event) =>
        !(
          event.type === 'error' &&
          FETCH_FAILURE_MESSAGE_RE.test(event.error?.message)
        ),
      clientToken: clientToken,
      compressIntakeRequests: true,
      defaultPrivacyLevel: 'allow',
      env: process.env.REACT_APP_ENVIRONMENT,
      plugins: [reactPlugin({router: true})],
      propagateTraceBaggage: false,
      service: 'track',
      sessionReplaySampleRate: 100,
      sessionSampleRate: 100,
      silentMultipleInit: true,
      storeContextsAcrossPages: true,
      trackingConsent: 'granted',
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      trackViewsManually: false,
      version: process.env.REACT_APP_VERSION,
    });

    datadogRum.onReady(() => {
      console.debug('Datadog RUM is ready');

      // Add global context for debugging
      datadogRum.setGlobalContext({
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });

      // Set up global error handlers after RUM is ready
      setupGlobalErrorHandlers();
    });
  } catch (error) {
    console.error('RUM initialization error:', error);
  }

  try {
    datadogLogs.init({
      beforeSend: (log) => {
        // EditorJS fires this when .off() is called before .on() — benign library quirk.
        if (log.message?.includes('EventDispatcher .off()')) return false;
        if (FETCH_FAILURE_MESSAGE_RE.test(log.message)) return false;
        // forwardErrorsToLogs also logs failed requests itself ("Fetch error
        // GET <url>"), with the thrown error only in the stack's first line.
        if (FETCH_FAILURE_MESSAGE_RE.test(log.error?.stack?.split('\n')[0]))
          return false;
        return true;
      },
      clientToken: clientToken,
      env: process.env.REACT_APP_ENVIRONMENT,
      forwardConsoleLogs: ['error', 'warn'], // Only forward errors and warnings
      forwardErrorsToLogs: true,
      service: 'track',
      sessionSampleRate: 100,
      silentMultipleInit: true,
      storeContextsAcrossPages: true,
      version: process.env.REACT_APP_VERSION,
    });

    console.debug('Datadog Logs initialized');
  } catch (error) {
    console.error('Logs initialization error:', error);
  }
};

/**
 * Get the current Datadog session ID for support reporting
 * Returns null if session ID is not available (e.g., localhost, synthetic, not initialized)
 */
export const getDatadogSessionId = () => {
  // Skip in development/synthetic environments
  if (shouldSkipDatadog()) {
    console.debug(
      'Session ID not available: development/synthetic environment'
    );
    return null;
  }

  // Check if RUM is initialized
  if (!window.DD_RUM) {
    console.warn('Session ID not available: Datadog RUM not initialized');
    return null;
  }

  try {
    // Get session ID from internal context
    const internalContext = datadogRum.getInternalContext();
    const sessionId = internalContext?.session_id;

    if (sessionId) {
      console.debug('Session ID retrieved:', sessionId);
      return sessionId;
    }

    // Session not available yet (page just loaded, session not started, etc.)
    console.warn('Session ID not available: Session not yet initialized');
    return null;
  } catch (error) {
    console.error('Error retrieving Datadog session ID:', error);
    return null;
  }
};

/**
 * Dynamically update the Datadog user
 */
export const setDatadogUser = (user) => {
  if (!user || shouldSkipDatadog()) return;

  const normalizedUser = buildDatadogUser(user);
  if (!normalizedUser) return;

  try {
    const rumUser = datadogRum.getUser();
    const logsUser = datadogLogs.getUser();
    const hasIncomingId = hasUsableDatadogId(normalizedUser);
    const hasExistingRumId = hasUsableDatadogId(rumUser);
    const hasExistingLogsId = hasUsableDatadogId(logsUser);

    if (!hasIncomingId) {
      console.debug('Skipping Datadog user update: no usable identifier');
      return;
    }

    if (!hasExistingRumId || rumUser.id !== normalizedUser.id) {
      console.debug(
        'Setting Datadog RUM user:',
        normalizedUser.id,
        normalizedUser.email
      );
      datadogRum.setUser({...normalizedUser});
    }
    if (!hasExistingLogsId || logsUser.id !== normalizedUser.id) {
      console.debug(
        'Setting Datadog Logs user:',
        normalizedUser.id,
        normalizedUser.email
      );
      datadogLogs.setUser({...normalizedUser});
    }
  } catch (error) {
    console.debug('Error setting Datadog user:', error);
  }
};

/**
 * Set up global error handlers
 */
export const setupGlobalErrorHandlers = () => {
  if (shouldSkipDatadog()) {
    console.debug(
      'Global error handlers skipped: Development/synthetic environment'
    );
    return;
  }

  // Global error handlers will be set up by the error logger
  // This function is now just a placeholder for future global handler setup
  console.debug('Global error handlers setup delegated to error logger');
};

/**
 * Export initialized Datadog instances for use in other modules
 */
export const getDatadogRum = () => {
  if (shouldSkipDatadog()) return null;
  return datadogRum;
};

export const getDatadogLogs = () => {
  if (shouldSkipDatadog()) return null;
  return datadogLogs;
};
